import { useQuery } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { useState } from 'react';

import { kinds, type EnvKey } from '@/app/site/scope';
import { siteEmails } from '@/shared/api/generated/site-users/site-users';
import type { EmailBody } from '@/shared/api/generated/model';
import { describeWhen } from '@/shared/lib/dates';
import { pluralize } from '@/shared/lib/plural';
import { Badge, type BadgeTone } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';

import styles from './UsersPage.module.scss';
import { EMAILS } from './words';

const PER_PAGE = 50;

const KINDS: Record<string, string> = {
  confirm_email: 'Подтверждение почты',
  reset_password: 'Сброс пароля',
  invite: 'Приглашение',
};

const STATUS: Record<EmailBody['status'], { label: string; tone: BadgeTone }> = {
  sent: { label: 'отправлено', tone: 'success' },
  queued: { label: 'отправляется', tone: 'neutral' },
  failed: { label: 'ошибка', tone: 'danger' },
  skipped: { label: 'почта не настроена', tone: 'warning' },
};

/**
 * Журнал писем посетителям: что, кому и чем кончилось. Пока почта (SMTP) не
 * настроена, письма не уходят — но текст со ссылкой виден здесь, и по нему
 * можно пройти регистрацию самому.
 */
export function EmailsList({ siteId, env }: { siteId: string; env: EnvKey }) {
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string>();

  const list = useQuery({
    queryKey: ['site-emails', siteId, env, page],
    queryFn: async () => {
      const response = await siteEmails(siteId, kinds[env], { page, per_page: PER_PAGE });

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на журнал писем');
      }

      return response.data;
    },
  });

  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PER_PAGE));

  if (list.isPending) {
    return null;
  }

  if (items.length === 0) {
    return (
      <div className={styles['tab']}>
        <EmptyState
          compact
          icon={<Mail />}
          title="Писем ещё не было"
          description="Здесь появятся письма посетителям: подтверждение почты, сброс пароля, приглашения."
        />
      </div>
    );
  }

  return (
    <div className={styles['tab']}>
      <ul className={styles['emails']}>
        {items.map((email) => {
          const status = STATUS[email.status];

          return (
            <li key={email.id} className={styles['email']}>
              <button
                type="button"
                className={styles['email__head']}
                aria-expanded={open === email.id}
                onClick={() => {
                  setOpen(open === email.id ? undefined : email.id);
                }}
              >
                <span className={styles['email__kind']}>{KINDS[email.kind] ?? email.kind}</span>
                <span className={styles['email__to']}>{email.to}</span>
                <Badge size="sm" tone={status.tone}>
                  {status.label}
                </Badge>
                <span className={styles['when']}>{describeWhen(email.created_at)}</span>
              </button>
              {open === email.id && (
                <div className={styles['email__body']}>
                  <p className={styles['email__subject']}>{email.subject}</p>
                  <pre>{email.body}</pre>
                  {email.error !== undefined && email.error !== '' && email.status === 'failed' && (
                    <p className={styles['error']}>{email.error}</p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {pages > 1 && (
        <div className={styles['pager']}>
          <Button
            size="sm"
            variant="outline"
            tone="neutral"
            disabled={page <= 1}
            onClick={() => {
              setPage(page - 1);
            }}
          >
            Новее
          </Button>
          <span>
            {page} из {pages} · {pluralize(total, EMAILS)}
          </span>
          <Button
            size="sm"
            variant="outline"
            tone="neutral"
            disabled={page >= pages}
            onClick={() => {
              setPage(page + 1);
            }}
          >
            Раньше
          </Button>
        </div>
      )}
    </div>
  );
}
